import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, BarChart3, Globe, Key, Zap, Hash, Server } from 'lucide-react';
import {
  MONO, SERIF, C, JsonBlock, CopyBtn, CodeTabs, TryItButton, buildCodeSamples,
} from '../components/codex/devkit';
import OnChainChip from '../components/OnChainChip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ────────────────────────────────────────────────────────────────────
 * Endpoint catalog. Single source of truth.
 * ──────────────────────────────────────────────────────────────────── */
const ENDPOINTS = [
  {
    id: 'analytics',
    method: 'GET',
    path: '/api/chancellor/analytics',
    samplePath: '/api/chancellor/analytics',
    title: 'Codex Analytics',
    summary: 'Global statistics across every judgment ever recorded — used to power /codex/analytics.',
    fields: [
      ['total_judgments', 'integer', 'Lifetime count of judgments stored.'],
      ['unique_wallets', 'integer', 'Distinct souls scanned at least once.'],
      ['current_season', 'integer', 'Active monthly season number.'],
      ['decree_types', 'array', '[{ decree_type, count }]'],
      ['top_behavior_tags', 'array', '[{ tag, count }] — top 10.'],
      ['activity_levels', 'array', '[{ activity_level, count }]'],
      ['risk_distribution', 'array', '[{ range, count }] — 0-25 / 25-50 / 50-75 / 75-100.'],
      ['activity_timeline_30d', 'array', '[{ day, count, season_number }]'],
      ['most_judged_this_season', 'array', '[{ wallet, judgments_count, reputation }] — top 10.'],
    ],
  },
  {
    id: 'leaderboard',
    method: 'GET',
    path: '/api/chancellor/leaderboard',
    samplePath: '/api/chancellor/leaderboard?limit=5',
    title: 'Reputation Leaderboard',
    summary: 'Wallets ranked by cumulative reputation across all judgments.',
    query: [
      ['limit', 'integer', '1-200', '50'],
      ['season', 'integer', 'optional season filter', '—'],
    ],
    fields: [
      ['items[].wallet', 'string', 'Solana address (base58).'],
      ['items[].reputation', 'integer', 'Sum of reputation_delta across judgments.'],
      ['items[].judgments_count', 'integer', 'Number of judgments recorded.'],
      ['items[].last_seen', 'string', 'ISO-8601 UTC.'],
      ['items[].highest_risk', 'integer', '0-100 max risk score observed.'],
      ['items[].last_decree', 'string', 'Most recent decree_type.'],
    ],
  },
  {
    id: 'wallet',
    method: 'GET',
    path: '/api/chancellor/wallet/{address}',
    samplePath: '/api/chancellor/wallet/C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4?limit=3',
    title: 'Wallet History',
    summary: 'Full judgment timeline for a single Solana wallet, newest first.',
    pathParams: [
      ['address', 'string', 'Base58 Solana address (32-44 chars).'],
    ],
    query: [
      ['limit', 'integer', '1-100', '25'],
      ['skip', 'integer', 'pagination offset', '0'],
    ],
    fields: [
      ['wallet', 'string', 'Echoed address.'],
      ['total', 'integer', 'Total judgments recorded for this wallet.'],
      ['items[].judgment_id', 'string', 'Stable identifier.'],
      ['items[].narration', 'string', 'Chancellor verdict text.'],
      ['items[].decree_type', 'string', 'trade narration · reputation update · ghost decree · coordination detected.'],
      ['items[].behavior_tags', 'string[]', 'e.g. steady, diversified, fresh entry.'],
      ['items[].risk_score', 'integer', '0-100.'],
      ['items[].activity_level', 'string', 'dormant · sleeping · casual · active · degen · whale.'],
      ['items[].reputation_delta', 'integer', 'Change applied to wallet reputation.'],
      ['items[].season_number', 'integer', 'Season this judgment belongs to.'],
      ['items[].tx_signature', 'string', 'Solana tx signature (devnet) — null when judgment was Demo-mode or pre-deploy.'],
      ['items[].tx_signature_is_mock', 'boolean', 'False when written on chain; true for Demo-mode / pre-deploy records.'],
      ['items[].snapshot', 'object', '{ sol_balance, portfolio_usd, distinct_tokens, recent_signatures, concentration_pct, activity_tier }'],
      ['items[].created_at', 'string', 'ISO-8601 UTC.'],
    ],
    errors: [
      ['400', 'Invalid Solana address (must be valid base58, 32-44 chars).'],
    ],
  },
  {
    id: 'season',
    method: 'GET',
    path: '/api/chancellor/season/{number}',
    samplePath: '/api/chancellor/season/3?limit=3',
    title: 'Season Judgments',
    summary: 'Every judgment recorded in a given season, newest first.',
    pathParams: [
      ['number', 'integer', 'Season number (1, 2, 3, …).'],
    ],
    query: [
      ['limit', 'integer', '1-200', '50'],
      ['skip', 'integer', 'pagination offset', '0'],
    ],
    fields: [
      ['season_number', 'integer', 'Echoed season.'],
      ['total', 'integer', 'Total judgments in this season.'],
      ['items', 'array', 'Same shape as /wallet items.'],
    ],
  },
];

/* ────────────────────────────────────────────────────────────────────
 * UI primitives
 * ──────────────────────────────────────────────────────────────────── */
const MethodChip = ({ method }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold tracking-[0.22em]"
    style={{
      fontFamily: MONO,
      color: C.green,
      background: 'rgba(20,40,20,0.85)',
      border: `1px solid ${C.green}55`,
      borderRadius: 2,
    }}
  >{method}</span>
);

const SectionLabel = ({ children }) => (
  <p
    className="text-[10px] tracking-[0.32em] mb-2"
    style={{ fontFamily: MONO, color: C.inkSoft }}
  >{children}</p>
);

// Definition table: 3 cols on desktop, stacked rows on mobile (CSS-driven so resize works).
const ParamTable = ({ rows, columns }) => {
  const cols = columns.map(c => c.w).join(' ');
  return (
    <>
      <style>{`
        .codex-paramtable .row { display: grid; grid-template-columns: 1fr; }
        @media (min-width: 640px) {
          .codex-paramtable .row { grid-template-columns: ${cols}; }
          .codex-paramtable .row .mobile-label { display: none; }
        }
      `}</style>
      <div
        className="codex-paramtable text-[11px]"
        style={{
          fontFamily: MONO,
          background: 'rgba(0,0,0,0.35)',
          border: `1px solid ${C.border}`,
        }}
      >
        <div
          className="hidden sm:grid px-3 py-2 gap-3"
          style={{
            gridTemplateColumns: cols,
            borderBottom: `1px solid ${C.border}`,
            color: C.inkMute,
            letterSpacing: '0.2em',
            fontSize: '10px',
          }}
        >
          {columns.map(c => <span key={c.label}>{c.label.toUpperCase()}</span>)}
        </div>
        {rows.map((row, i) => (
          <div
            key={i}
            className="row px-3 py-2 gap-x-3 gap-y-1"
            style={{
              borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : 'none',
            }}
          >
            {row.map((cell, j) => {
              const col = columns[j];
              return (
                <span
                  key={j}
                  className="break-words"
                  style={{
                    color: col.color || C.ink,
                    fontFamily: col.mono ? MONO : SERIF,
                    fontStyle: col.italic ? 'italic' : 'normal',
                    fontSize: col.mono ? '11.5px' : '12.5px',
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    className="mobile-label text-[9px] mr-1.5"
                    style={{ color: C.inkMute, fontFamily: MONO, letterSpacing: '0.15em' }}
                  >
                    {col.label.toUpperCase()}
                  </span>
                  {cell}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Endpoint Section
 * ──────────────────────────────────────────────────────────────────── */
function EndpointSection({ ep }) {
  const samples = buildCodeSamples({ baseUrl: API_URL, path: ep.path, samplePath: ep.samplePath });
  const sampleUrl = `${API_URL}${ep.samplePath || ep.path}`;
  return (
    <section
      id={ep.id}
      data-testid={`api-endpoint-${ep.id}`}
      className="scroll-mt-20 mb-14"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <MethodChip method={ep.method} />
        <code className="text-[13px] sm:text-[14px] break-all" style={{ color: C.gold, fontFamily: MONO }}>
          {ep.path}
        </code>
      </div>
      <h2
        className="text-xl sm:text-2xl font-bold mb-1"
        style={{ fontFamily: "'Cinzel', serif", color: C.ink, letterSpacing: '0.02em' }}
      >{ep.title}</h2>
      <p
        className="italic mb-5"
        style={{ fontFamily: SERIF, color: C.inkSoft, fontSize: '14px', lineHeight: 1.5 }}
      >{ep.summary}</p>

      {/* Code tabs (curl / JS / Python) */}
      <div className="mb-3">
        <CodeTabs samples={samples} idPrefix={`api-${ep.id}`} />
      </div>

      {/* Try it live */}
      <TryItButton url={sampleUrl} idPrefix={`api-${ep.id}`} />

      {/* Path params */}
      {ep.pathParams && (
        <div className="mt-6">
          <SectionLabel>PATH PARAMETERS</SectionLabel>
          <ParamTable
            rows={ep.pathParams}
            columns={[
              { label: 'Name', w: '160px', mono: true, color: C.gold },
              { label: 'Type', w: '110px', mono: true, color: C.violet },
              { label: 'Description', w: '1fr', italic: true, color: C.ink },
            ]}
          />
        </div>
      )}

      {/* Query params */}
      {ep.query && (
        <div className="mt-5">
          <SectionLabel>QUERY PARAMETERS</SectionLabel>
          <ParamTable
            rows={ep.query}
            columns={[
              { label: 'Name', w: '140px', mono: true, color: C.gold },
              { label: 'Type', w: '90px', mono: true, color: C.violet },
              { label: 'Description', w: '1fr', italic: true, color: C.ink },
              { label: 'Default', w: '90px', mono: true, color: C.cyan },
            ]}
          />
        </div>
      )}

      {/* Response schema */}
      <div className="mt-5">
        <SectionLabel>RESPONSE · 200 OK</SectionLabel>
        <ParamTable
          rows={ep.fields}
          columns={[
            { label: 'Field', w: '260px', mono: true, color: C.gold },
            { label: 'Type', w: '120px', mono: true, color: C.violet },
            { label: 'Description', w: '1fr', italic: true, color: C.ink },
          ]}
        />
      </div>

      {/* Errors */}
      {ep.errors && (
        <div className="mt-5">
          <SectionLabel>ERRORS</SectionLabel>
          <ParamTable
            rows={ep.errors}
            columns={[
              { label: 'Status', w: '90px', mono: true, color: C.red },
              { label: 'Description', w: '1fr', italic: true, color: C.ink },
            ]}
          />
        </div>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Main page
 * ──────────────────────────────────────────────────────────────────── */
export default function ApiDocsPage() {
  const [active, setActive] = useState(ENDPOINTS[0].id);
  const sectionRefs = useRef({});

  // Sidebar active-section tracker on scroll.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    ENDPOINTS.forEach((ep) => {
      const el = document.getElementById(ep.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const baseCurl = `curl -s "${API_URL}/api/chancellor/analytics"`;

  return (
    <div className="min-h-screen relative" style={{ background: C.bg, color: C.ink }}>
      {/* Atmospheric backdrop — quieter than before so code stays legible. */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 80% 0%, rgba(140,30,30,0.10) 0%, transparent 50%),
            radial-gradient(ellipse at 0% 100%, rgba(120,80,30,0.06) 0%, transparent 60%)
          `,
        }}
      />
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" width=\\"180\\" height=\\"180\\"><filter id=\\"n\\"><feTurbulence baseFrequency=\\"0.85\\"/></filter><rect width=\\"100%\\" height=\\"100%\\" filter=\\"url(%23n)\\"/></svg>")',
        }}
      />

      {/* Top bar */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: 'rgba(8,4,4,0.78)',
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-4">
          <Link
            to="/halls"
            data-testid="api-docs-back"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] hover:brightness-125 transition"
            style={{ color: C.inkSoft, fontFamily: MONO }}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            HALLS
          </Link>
          <span style={{ color: C.inkMute, fontFamily: MONO, fontSize: 11 }}>·</span>
          <span
            className="text-[11px] tracking-[0.22em]"
            style={{ color: C.gold, fontFamily: MONO }}
          >
            CODEX API
          </span>
          <div className="flex-1" />
          <Link
            to="/codex/analytics"
            data-testid="api-docs-analytics-link"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] hover:brightness-125 transition"
            style={{ color: C.inkSoft, fontFamily: MONO }}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            ANALYTICS
          </Link>
        </div>
      </header>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-10 pb-32 lg:flex lg:gap-10">

        {/* ─── Sidebar ─── */}
        <aside
          className="hidden lg:block lg:w-56 flex-shrink-0"
          style={{ position: 'sticky', top: 64, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}
          data-testid="api-docs-sidebar"
        >
          <p className="text-[10px] tracking-[0.32em] mb-3" style={{ fontFamily: MONO, color: C.inkMute }}>
            ON THIS PAGE
          </p>
          <nav className="space-y-0.5">
            <a
              href="#getting-started"
              className="block py-1 text-[12px] tracking-wide hover:brightness-150 transition"
              style={{ fontFamily: MONO, color: active === 'getting-started' ? C.gold : C.inkSoft }}
            >
              Getting Started
            </a>
            {ENDPOINTS.map(ep => (
              <a
                key={ep.id}
                href={`#${ep.id}`}
                data-testid={`api-docs-toc-${ep.id}`}
                className="block py-1 text-[12px] tracking-wide hover:brightness-150 transition"
                style={{
                  fontFamily: MONO,
                  color: active === ep.id ? C.gold : C.inkSoft,
                  borderLeft: `2px solid ${active === ep.id ? C.gold : 'transparent'}`,
                  paddingLeft: 8,
                  marginLeft: -10,
                }}
              >
                {ep.title}
              </a>
            ))}
          </nav>

          <p className="text-[10px] tracking-[0.32em] mt-8 mb-3" style={{ fontFamily: MONO, color: C.inkMute }}>
            RESOURCES
          </p>
          <Link
            to="/codex/analytics"
            className="block py-1 text-[12px] tracking-wide hover:brightness-150 transition"
            style={{ fontFamily: MONO, color: C.inkSoft }}
          >Analytics dashboard</Link>
          <Link
            to="/halls"
            className="block py-1 text-[12px] tracking-wide hover:brightness-150 transition"
            style={{ fontFamily: MONO, color: C.inkSoft }}
          >Back to the Halls</Link>
        </aside>

        {/* ─── Main column ─── */}
        <main className="flex-1 min-w-0">

          {/* Hero */}
          <section className="mb-12">
            <p className="text-[10px] tracking-[0.4em] mb-3" style={{ color: C.inkSoft, fontFamily: MONO }}>
              · CODEX · PUBLIC API ·
            </p>
            <h1
              data-testid="api-docs-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight"
              style={{
                fontFamily: "'Cinzel', serif",
                color: C.ink,
                letterSpacing: '0.01em',
                textShadow: '0 0 24px rgba(255,180,80,0.18)',
              }}
            >Read the Ledger.</h1>
            <p
              className="max-w-2xl text-[15px] leading-relaxed mb-4"
              style={{ fontFamily: SERIF, color: C.inkSoft }}
            >
              Four endpoints. <span style={{ color: C.gold }}>No keys.</span>{' '}
              <span style={{ color: C.gold }}>No rate-card.</span> Every wallet judged by the Chancellor
              becomes part of a public, machine-readable dataset — free for traders, dashboards, and
              researchers to query.
            </p>
            <OnChainChip target="program" testId="api-docs-onchain-chip" className="mb-6" />

            <div
              className="grid grid-cols-3 gap-3 max-w-xl"
              data-testid="api-docs-features"
            >
              {[
                { Icon: Globe, label: 'PUBLIC', sub: 'no auth' },
                { Icon: Zap, label: 'JSON', sub: 'one shape' },
                { Icon: Server, label: 'STABLE', sub: 'versioned' },
              ].map(({ Icon, label, sub }) => (
                <div
                  key={label}
                  className="px-3 py-2.5 flex items-center gap-2.5"
                  style={{
                    background: C.bgPanel,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: C.gold }} />
                  <div>
                    <p className="text-[10.5px] font-bold tracking-[0.22em]" style={{ color: C.ink, fontFamily: MONO }}>{label}</p>
                    <p className="text-[9px] tracking-[0.18em]" style={{ color: C.inkMute, fontFamily: MONO }}>{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Getting started */}
          <section id="getting-started" data-testid="api-docs-getting-started" className="scroll-mt-20 mb-14">
            <p className="text-[10px] tracking-[0.32em] mb-2" style={{ fontFamily: MONO, color: C.inkSoft }}>GETTING STARTED</p>
            <h2 className="text-xl sm:text-2xl font-bold mb-3" style={{ fontFamily: "'Cinzel', serif", color: C.ink }}>
              Make your first call
            </h2>

            <div className="mb-5">
              <p className="text-[11px] tracking-[0.2em] mb-2" style={{ fontFamily: MONO, color: C.inkSoft }}>BASE URL</p>
              <div
                className="flex items-center gap-3 px-3 py-2.5"
                style={{
                  background: C.bgCode,
                  border: `1px solid ${C.border}`,
                  borderLeft: `3px solid ${C.gold}`,
                }}
              >
                <Hash className="w-3.5 h-3.5 flex-shrink-0" style={{ color: C.gold }} />
                <code className="text-[12.5px] break-all flex-1 min-w-0" style={{ color: C.ink, fontFamily: MONO }}>{API_URL}</code>
                <CopyBtn text={API_URL} testId="api-docs-copy-base-url" />
              </div>
            </div>

            <p className="text-[11px] tracking-[0.2em] mb-2" style={{ fontFamily: MONO, color: C.inkSoft }}>TRY IT</p>
            <div
              className="flex items-center gap-3 px-3 py-2.5"
              style={{
                background: C.bgCode,
                border: `1px solid ${C.border}`,
              }}
            >
              <code className="text-[12.5px] break-all flex-1 min-w-0" style={{ color: C.green, fontFamily: MONO }}>$ {baseCurl}</code>
              <CopyBtn text={baseCurl} testId="api-docs-copy-quickstart" />
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { Icon: Key, h: 'No authentication', p: 'No API key, no signed request, no allowlist. Just GET.' },
                { Icon: Globe, h: 'CORS open', p: 'Browsers can call directly. Build a frontend on top.' },
              ].map(({ Icon, h, p }) => (
                <div key={h} className="px-4 py-3" style={{ background: C.bgPanel, border: `1px solid ${C.border}` }}>
                  <Icon className="w-3.5 h-3.5 mb-1.5" style={{ color: C.gold }} />
                  <p className="text-[12px] font-bold mb-0.5" style={{ color: C.ink, fontFamily: SERIF }}>{h}</p>
                  <p className="text-[12px] italic" style={{ color: C.inkSoft, fontFamily: SERIF, lineHeight: 1.4 }}>{p}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Endpoints */}
          {ENDPOINTS.map(ep => <EndpointSection key={ep.id} ep={ep} />)}

          {/* Footer */}
          <footer
            className="mt-16 pt-6 text-center"
            style={{ borderTop: `1px solid ${C.border}` }}
          >
            <p className="text-[10px] tracking-[0.3em]" style={{ fontFamily: MONO, color: C.inkMute }}>
              ◆ THE CODEX SPEAKS · DATASET GROWS WITH EVERY SCAN ◆
            </p>
            <p className="text-[10px] mt-2" style={{ fontFamily: MONO, color: C.inkMute }}>
              Need richer data?{' '}
              <Link to="/halls" className="underline hover:brightness-150" style={{ color: C.inkSoft }}>
                Visit the Halls of the Chancellor
              </Link>.
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}
